import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { LogOut, ClipboardList, Star, Calendar, Bell, Trophy, CheckCircle, XCircle, Brain, User, TrendingUp, Video } from 'lucide-react';
import Notifications from '../components/Notifications';
import MentalGames from '../components/MentalGames';
import StudentProfile from '../components/StudentProfile';
import StudentLiveExam from '../components/StudentLiveExam';
import StudentProgress from '../components/StudentProgress';
import OnlineClassView from '../components/OnlineClassView';
import { Progress } from '../components/ui/progress';

const StudentDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tasks, setTasks] = useState([]);
  const [stars, setStars] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [activeClasses, setActiveClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [lastMonthAchievement, setLastMonthAchievement] = useState(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadData();
    loadNotifications();
    loadLastMonthAchievement();
    loadActiveClasses();
    // Poll for active classes every 30 seconds
    const interval = setInterval(loadActiveClasses, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, starsRes, attendanceRes, rankingRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/stars/student/${user.id}`),
        axios.get(`${API}/attendance/student/${user.id}`),
        axios.get(`${API}/reports/ranking?month=${new Date().getMonth() + 1}&year=${new Date().getFullYear()}`)
      ]);
      setTasks(tasksRes.data);
      setStars(starsRes.data);
      setAttendance(attendanceRes.data);
      setRanking(rankingRes.data);
    } catch (error) {
      console.error('Failed to load data', error);
    }
  };

  const loadActiveClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/online-classes/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveClasses(response.data);
    } catch (error) {
      console.error('Failed to load active classes', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications', error);
    }
  };

  const loadLastMonthAchievement = async () => {
    try {
      const response = await axios.get(`${API}/attendance/achievements/last-month`);
      if (response.data.total_achievers > 0) {
        setLastMonthAchievement(response.data.achievers[0]);
      }
    } catch (error) {
      console.error('Failed to load achievements', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`);
      toast.success('Task marked as completed!');
      loadData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const totalStars = stars.reduce((sum, s) => sum + s.stars, 0);
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const pendingTasks = tasks.filter(t => t.status === 'pending').length;
  const presentDays = attendance.filter(a => a.status === 'present').length;
  const myRank = ranking.find(r => r.student_id === user.id);

  // If viewing a class, show OnlineClassView
  if (selectedClassId) {
    return <OnlineClassView classId={selectedClassId} onExit={() => setSelectedClassId(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="student-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.svg" 
                alt="GeniusKid" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>GeniusKid</h1>
                <p className="text-sm text-gray-600">Hello, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setActiveTab('notifications')}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              <Button
                onClick={() => setActiveTab('profile')}
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="profile-button"
              >
                <User className="w-4 h-4" />
                <span>My Profile</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-2 mb-8 bg-white rounded-xl p-2 shadow-sm">
          {[
            { id: 'overview', label: 'Overview', icon: Trophy },
            { id: 'tasks', label: 'My Tasks', icon: ClipboardList },
            { id: 'performance', label: 'Performance', icon: Star },
            { id: 'attendance', label: 'Attendance', icon: Calendar }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-fade-in">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg card-hover" data-testid="stat-stars">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm mb-1">Total Stars</p>
                    <p className="text-3xl font-bold">{totalStars}</p>
                  </div>
                  <Star className="w-12 h-12 text-amber-200 opacity-80" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg card-hover" data-testid="stat-completed">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm mb-1">Completed</p>
                    <p className="text-3xl font-bold">{completedTasks}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-orange-200 opacity-80" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-rose-500 to-rose-600 text-white border-0 shadow-lg card-hover" data-testid="stat-pending">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-rose-100 text-sm mb-1">Pending</p>
                    <p className="text-3xl font-bold">{pendingTasks}</p>
                  </div>
                  <XCircle className="w-12 h-12 text-rose-200 opacity-80" />
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg card-hover" data-testid="stat-rank">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm mb-1">My Rank</p>
                    <p className="text-3xl font-bold">#{myRank?.rank || '-'}</p>
                  </div>
                  <Trophy className="w-12 h-12 text-purple-200 opacity-80" />
                </div>
              </Card>
            </div>

            {/* Active Online Classes */}
            {activeClasses.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Active Online Classes
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeClasses.map(cls => (
                    <Card key={cls.id} className="p-6 bg-gradient-to-br from-blue-500 to-purple-500 text-white border-0 shadow-lg">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold mb-1">{cls.batch_name}</h3>
                          <p className="text-sm text-white/80">with {cls.teacher_name}</p>
                        </div>
                        <Video className="w-8 h-8 text-white/80" />
                      </div>
                      <Button 
                        onClick={() => setSelectedClassId(cls.id)}
                        className="w-full bg-white text-blue-600 hover:bg-white/90 font-semibold"
                      >
                        Join Class
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Live Exam Sessions */}
            <StudentLiveExam user={user} />

            {/* Mental Games Card */}
            <Card 
              className="p-6 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white border-0 shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              onClick={() => setActiveTab('games')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Brain className="w-8 h-8" />
                    <h3 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Mental Games
                    </h3>
                  </div>
                  <p className="text-purple-100 mb-4">Train your brain with fun challenges!</p>
                  <div className="flex gap-4 text-sm">
                    <div className="bg-white/20 px-3 py-1 rounded-full">⚡ Flash Numbers</div>
                    <div className="bg-white/20 px-3 py-1 rounded-full">🔢 Math Operations</div>
                  </div>
                </div>
                <div className="text-6xl opacity-20">🧠</div>
              </div>
            </Card>

            {/* Last Month Achievement Badge */}
            {lastMonthAchievement && (
              <Card 
                className="p-6 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 text-white border-0 shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300"
                onClick={() => setActiveTab('profile')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Trophy className="w-8 h-8" />
                      <h3 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        100% Attendance Achiever!
                      </h3>
                    </div>
                    <p className="text-yellow-100 mb-2">
                      Congratulations on perfect attendance last month! 🎉
                    </p>
                    <div className="flex gap-4 text-sm">
                      <div className="bg-white/20 px-3 py-1 rounded-full">
                        {new Date(0, lastMonthAchievement.month - 1).toLocaleString('default', { month: 'long' })} {lastMonthAchievement.year}
                      </div>
                      <div className="bg-white/20 px-3 py-1 rounded-full">
                        {lastMonthAchievement.total_classes} Classes
                      </div>
                    </div>
                  </div>
                  <div className="text-6xl">🏆</div>
                </div>
              </Card>
            )}

            {/* My Progress */}
            <Card className="p-6 bg-white shadow-lg border-0">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Task Completion</span>
                    <span className="text-sm text-gray-600">{completedTasks}/{tasks.length}</span>
                  </div>
                  <Progress value={tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Attendance Rate</span>
                    <span className="text-sm text-gray-600">{presentDays} days</span>
                  </div>
                  <Progress value={presentDays > 0 ? Math.min((presentDays / 20) * 100, 100) : 0} className="h-2" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="space-y-4" data-testid="tasks-section">
            <Card className="p-6 bg-white shadow-lg border-0">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Tasks</h3>
              <div className="space-y-3">
                {tasks.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No tasks assigned yet</p>
                ) : (
                  tasks.map(task => (
                    <Card key={task.id} className="p-4 border-2 hover:shadow-md transition-shadow" data-testid={`task-${task.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-gray-900">{task.title}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {task.priority}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                          <p className="text-xs text-gray-500">Deadline: {new Date(task.deadline).toLocaleDateString()}</p>
                        </div>
                        {task.status === 'pending' && (
                          <Button
                            onClick={() => handleCompleteTask(task.id)}
                            size="sm"
                            className="ml-4 bg-green-500 hover:bg-green-600"
                            data-testid={`complete-task-${task.id}`}
                          >
                            Mark Complete
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-4" data-testid="performance-section">
            {/* Star History */}
            <Card className="p-6 bg-white shadow-lg border-0">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Stars</h3>
              <div className="space-y-3">
                {stars.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No stars received yet</p>
                ) : (
                  stars.map(star => (
                    <Card key={star.id} className="p-4 border-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${
                                  i < star.stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          {star.comment && <p className="text-sm text-gray-600">{star.comment}</p>}
                          <p className="text-xs text-gray-500 mt-1">{new Date(star.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </Card>

            {/* Monthly Ranking */}
            <Card className="p-6 bg-white shadow-lg border-0">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Monthly Ranking</h3>
              <div className="space-y-2">
                {ranking.slice(0, 10).map((rank, idx) => (
                  <div
                    key={rank.student_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      rank.student_id === user.id ? 'bg-amber-100 border-2 border-amber-400' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-white' :
                        idx === 1 ? 'bg-gray-400 text-white' :
                        idx === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {rank.rank}
                      </span>
                      <span className="font-medium">{rank.student_name}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-gray-600">{rank.total_stars} ⭐</span>
                      <span className="font-bold text-gray-900">{rank.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'attendance' && (
          <Card className="p-6 bg-white shadow-lg border-0" data-testid="attendance-section">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Attendance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {attendance.length === 0 ? (
                <p className="text-gray-500 col-span-full text-center py-8">No attendance records yet</p>
              ) : (
                attendance.map(att => (
                  <Card key={att.id} className={`p-4 border-2 ${
                    att.status === 'present' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{new Date(att.date).toLocaleDateString()}</p>
                        <p className="text-sm text-gray-600">{att.status === 'present' ? 'Present' : 'Absent'}</p>
                      </div>
                      {att.status === 'present' ? (
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-500" />
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          </Card>
        )}

        {activeTab === 'notifications' && <Notifications notifications={notifications} onUpdate={loadNotifications} />}
        
        {activeTab === 'profile' && (
          <div className="animate-fade-in">
            <StudentProfile user={user} />
          </div>
        )}
        
        {activeTab === 'games' && (
          <div className="animate-fade-in">
            <MentalGames user={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;